import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

function About({ isMobile }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const imageVariants = {
    hidden: { scale: 0.8, opacity: 0, rotateY: -20 },
    visible: {
      scale: 1,
      opacity: 1,
      rotateY: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        delay: 0.5
      }
    },
    hover: {
      scale: 1.03,
      rotateY: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      y: {
        repeat: Infinity,
        duration: 3,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section 
      id="about" 
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 50%, #0c0c0c 100%)'
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-gradient-to-r from-amber-900/10 to-red-900/10 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-gradient-to-r from-red-900/10 to-amber-900/10 blur-3xl"></div>
        
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        ></div>
      </div>

      {/* Floating Meat Icons */}
      <motion.div 
        animate={floatAnimation}
        className="absolute top-20 left-10 hidden lg:block"
      >
        <div className="text-amber-500/20 text-6xl">🥩</div>
      </motion.div>
      
      <motion.div 
        animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 1 } }}
        className="absolute bottom-20 right-10 hidden lg:block"
      >
        <div className="text-red-500/20 text-6xl">🍖</div>
      </motion.div>

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-12 items-center`}
        >
          <motion.div 
            variants={itemVariants}
            className={`${isMobile ? 'w-full' : 'w-1/2'} relative`}
          >
            {/* Decorative Badge */}
            <div className="absolute -top-6 -left-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-red-600 rounded-full blur-sm"></div>
                <div className="relative bg-gradient-to-r from-amber-500 to-red-500 text-white px-6 py-2 rounded-full text-sm font-bold transform -rotate-6">
                  SINCE 2025
                </div>
              </div>
            </div>

            {/* Glowing Text Effect */}
            <motion.h2 
              variants={itemVariants}
              className={`font-bold mb-6 relative ${isMobile ? 'text-4xl' : 'text-5xl'}`}
              style={{
                background: 'linear-gradient(45deg, #fbbf24, #dc2626, #fbbf24)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradientShift 3s ease infinite'
              }}
            >
              Pork Specialists
              <div className="absolute -bottom-2 left-0 w-32 h-1 bg-gradient-to-r from-amber-500 to-red-500 rounded-full"></div>
            </motion.h2>

            <motion.div 
              variants={itemVariants}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/5 to-red-500/5 rounded-3xl blur-xl"></div>
              <div className="relative bg-gradient-to-b from-gray-900 to-black p-8 rounded-2xl border border-gray-800 shadow-2xl">
                <motion.p 
                  variants={itemVariants}
                  className="text-gray-300 mb-6 text-lg leading-relaxed"
                >
                  Sir Henry Butchery is dedicated to providing <span className="text-amber-300 font-semibold">premium quality pork</span>, beef and chicken at competitive prices. Based in Zvido Business Centre, we specialize in <span className="text-red-300 font-semibold">artisanal pork cuts</span> to serve both restaurants and individual customers.
                </motion.p>
                
                <motion.div 
                  variants={itemVariants}
                  className="flex flex-wrap gap-4 mb-6"
                >
                  <span className="px-4 py-2 bg-gradient-to-r from-amber-900/30 to-amber-600/30 border border-amber-700/50 rounded-full text-amber-200 text-sm font-medium">
                    🥩 Premium Cuts
                  </span>
                  <span className="px-4 py-2 bg-gradient-to-r from-red-900/30 to-red-600/30 border border-red-700/50 rounded-full text-red-200 text-sm font-medium">
                    🔪 Expert Butchery
                  </span>
                  <span className="px-4 py-2 bg-gradient-to-r from-gray-900 to-gray-800 border border-gray-700 rounded-full text-gray-300 text-sm font-medium">
                    ⭐ 100% Satisfaction
                  </span>
                </motion.div>

                {/* Stats */}
                <motion.div 
                  variants={itemVariants}
                  className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-8"
                >

                  {/*<div className="text-center">
                    <div className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text text-transparent">500+</div>
                    <div className="text-gray-400 text-sm">Happy Clients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">50+</div>
                    <div className="text-gray-400 text-sm">Restaurants Served</div>
                  </div>
                  */}
                  <div className="text-center col-span-2 md:col-span-1">
                    <div className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-red-400 bg-clip-text text-transparent">Daily</div>
                    <div className="text-gray-400 text-sm">Fresh Supply</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div 
            variants={imageVariants}
            animate={controls}
            whileHover="hover"
            className={`${isMobile ? 'w-full mt-8' : 'w-1/2'} relative`}
          >
            {/* Image Container with Glow */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 via-red-500/20 to-amber-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Main Image */}
              <motion.div
                className="relative overflow-hidden rounded-2xl shadow-2xl"
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1200&q=80"
                  alt="Premium pork selection at Sir Henry's Butchery" 
                  className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                
                {/* Floating Labels */}
                <div className="absolute top-4 left-4">
                  <div className="bg-gradient-to-r from-amber-600 to-red-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    PREMIUM CUTS
                  </div>
                </div>
              </motion.div>

              {/* Decorative Corner Elements */}
              <div className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-amber-500"></div>
              <div className="absolute -top-3 -right-3 w-6 h-6 border-t-2 border-r-2 border-red-500"></div>
              <div className="absolute -bottom-3 -left-3 w-6 h-6 border-b-2 border-l-2 border-amber-500"></div>
              <div className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-red-500"></div>
            </div>

            {/* Floating Testimonial Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2 }}
              className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-black p-4 rounded-xl shadow-2xl border border-gray-800 max-w-xs hidden lg:block"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-red-500 flex items-center justify-center">
                  <span className="text-white font-bold">⭐</span>
                </div>
                <div>
                  <div className="font-bold text-white">Trusted Chef</div>
                  <div className="text-amber-400 text-sm">⭐⭐⭐⭐⭐</div>
                </div>
              </div>
              <p className="text-gray-300 text-sm italic">
                "The quality of pork cuts here is unmatched. My restaurant's signature dish depends on it!"
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .shadow-glow {
          box-shadow: 0 0 40px rgba(251, 191, 36, 0.2), 0 0 80px rgba(220, 38, 38, 0.1);
        }
        
        .text-gradient {
          background: linear-gradient(45deg, #fbbf24, #dc2626, #fbbf24);
          background-size: 200% 200%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradientShift 3s ease infinite;
        }
        
        .border-gradient {
          border: 2px solid transparent;
          background: linear-gradient(45deg, #fbbf24, #dc2626) border-box;
          -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
        }
      `}</style>
    </section>
  );
}

export default About;